import * as R from 'rambdax'
import {deepMerge, isObjectType} from 'tizra'
import {PartialDeep, SetRequired} from 'type-fest'
import * as A from '../admin'
import {blockMeta} from '../meta'

interface V0 extends A.BlockConfig {
  alignment: 'left' | 'center' | 'right'
  heading: string
  message: string
}

interface V1 extends A.BlockConfig {
  VERSION: 1
  heading: string
  message: string
  justification: 'left' | 'center' | 'right'
  position: 'left' | 'center' | 'right'
}

interface V2 extends A.BlockConfig {
  VERSION: 2
  heading: string
  message: string
  justification: A.Justification
  container: 'default' | 'wider' | 'none'
  background: A.BackgroundSpec
}

const defaultV2: V2 = {
  VERSION: 2,
  heading: '',
  message: 'Hello World',
  container: 'default',
  justification: 'left',
  background: A.defaultBackgroundSpec,
  FREEMARKER: {
    ...A.backgroundSpecFREEMARKER('background'),
    message: 'markdown',
  },
}

export type MarkdownBlockConfig = V2

const defaultConfig = defaultV2

const posToCardinal = (pos?: 'left' | 'center' | 'right'): A.CardinalX =>
  pos === 'center' ? ''
  : pos === 'right' ? 'e'
  : 'w'

type PV<T extends {VERSION: any}> = SetRequired<PartialDeep<T>, 'VERSION'>

const defaultV1: V1 = {
  VERSION: 1,
  heading: '',
  message: '',
  justification: 'left',
  position: 'left',
}

const zeroToOne = <C extends PartialDeep<V0>>(config: C): V1 =>
  deepMerge(defaultV1)({
    heading: config.heading,
    message: config.message,
    justification: config.alignment,
    position: config.alignment,
  })

const oneToTwo = <C extends PV<V1>>(config: C): V2 =>
  deepMerge(defaultV2)({
    heading: config.heading,
    message: config.message,
    justification: config.justification,
    background: {
      foreground: {
        position: posToCardinal(config.position),
      },
    },
  })

const isV0 = (config: unknown): config is PartialDeep<V0> =>
  isObjectType(config) && !('VERSION' in config)

const isV1 = (config: unknown): config is PV<V1> =>
  (config as any)?.VERSION === 1

const migrate: A.Migrate<
  MarkdownBlockConfig,
  undefined | PartialDeep<V0> | PV<V1> | PV<V2>
> = ({config}) =>
  R.piped(
    config,
    config => (isV0(config) ? zeroToOne(config) : config),
    config =>
      isV1(config) ? oneToTwo(config) : deepMerge(defaultConfig)(config),
  )

export const meta = blockMeta({
  name: 'blurb',
  displayName: 'MarkdownBlock',
  title: 'Markdown',
  defaultConfig,
  migrate,
})
